import React from 'react';
import gql from 'graphql-tag';
import { graphql } from '@apollo/react-hoc';
import PropTypes from 'prop-types';
import addMonths from 'date-fns/addMonths';
import format from 'date-fns/format';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import some from 'lodash/some';
import Grid from '../../components/Grid';
import DateTimeAgenda from '../RepVisitsCalendarEventDetailsAgenda/date-time';
import EventAgenda from '../../components/CalendarEventAgenda';
import Checkbox from '../../components/Checkbox';
import Message from '../../components/Message';
import Label from '../../components/Label';
import Icon from '../../components/Icon';
import Button from '../../components/Button';
import messages from './messages';
import './styles.css';
import generateEvents from '../AvailabilityCalendar/date-utils';
import { SimpleLoadingComponent } from '../../utils/apollo';

function getWCAGAppointmentType(rsvp) {
  if (rsvp.pendingStatus) {
    return 'A pending appointment';
  } else if (rsvp.type === 'personal' || rsvp.type === 'visit') {
    return 'Confirmed visit';
  } else if (rsvp.type === 'fair') {
    return 'Confirmed fair';
  }
  return 'Visit type not found';
}

export class AppointmentsListComponent extends React.PureComponent {
  static defaultItemsToShow = 25;

  static defaultMonthsToFetch = 18;

  static propTypes = {
    viewer: PropTypes.object,
    onSelect: PropTypes.func.isRequired,
    selectedStaffName: PropTypes.string.isRequired,
    selectedAppointments: PropTypes.array.isRequired,
    selectAllToggle: PropTypes.func.isRequired,
    selectBoxError: PropTypes.bool.isRequired,
    hasAppointments: PropTypes.func,
    heUserId: PropTypes.string.isRequired,
  };

  state = {
    itemsToShow: AppointmentsListComponent.defaultItemsToShow,
  };

  componentDidMount = () => {
    this.props.hasAppointments(this.rsvps.length);
  }

  componentWillReceiveProps = (nextProps) => {
    const { viewer } = nextProps;
    const rsvps = get(viewer, 'repVisitsEvents', []);
    this.props.hasAppointments(rsvps.length);
  };

  onSelect = (event) => {
    this.props.onSelect(event);
  };

  selectAllToggle = (events) => {
    this.props.selectAllToggle(events);
  };

  showMore = () => {
    /* istanbul ignore if */
    if (this.state.itemsToShow >= AppointmentsListComponent.defaultItemsToShow) {
      // eslint-disable-next-line react/no-access-state-in-setstate
      this.setState({ itemsToShow: this.state.itemsToShow + AppointmentsListComponent.defaultItemsToShow });
    }
  };

  _relayKey = (event) => {
    const { appointmentId, dayId, collegeFairId } = event;
    return `${appointmentId}_${dayId || collegeFairId}`;
  };

  render() {
    const { viewer } = this.props;
    const rsvps = generateEvents(get(viewer, 'repVisitsEvents', []), viewer.enabledFeatures);
    this.rsvps = rsvps;
    const countToShow = rsvps.length >= AppointmentsListComponent.defaultItemsToShow ? this.state.itemsToShow : rsvps.length;
    const showingAppointments = rsvps.length <= countToShow ? `${rsvps.length}`:`${countToShow}`;
    return (
      <div>
        <div>
          {rsvps.length > 0 ? (
            <div>
              <p styleName="subheadingContent">Showing all of {`${this.props.selectedStaffName}'s`} appointments
                <div styleName="selectall">
                  <Checkbox
                    label={{
                      htmlFor: 'selectAllCheckBox',
                      children: `Select all (${rsvps.length})`,
                    }}
                    id="selectAllCheckBox"
                    checked={this.props.selectedAppointments.length === rsvps.length}
                    onClick={() => this.selectAllToggle(rsvps)}
                  />
                </div>
              </p>
              <ul styleName="appointmentresults">
                {rsvps.slice(0, this.state.itemsToShow).map((rsvp) => (
                  <Grid as="li" tabIndex="0" styleName="faircolumn" key={this._relayKey(rsvp)}>
                    <Grid.Row styleName="row-details">
                      <Grid.Column width={4} styleName="fairfirstcolumn">
                        <p styleName="visuallyHidden">{getWCAGAppointmentType(rsvp)}</p>
                        <DateTimeAgenda event={rsvp} enabledFeatures={viewer.enabledFeatures} />
                      </Grid.Column>
                      <Grid.Column width={12}>
                        <div styleName="select">
                          <Checkbox
                            id={rsvp.id}
                            data-cy="repvisits-appointments-list-checkbox"
                            aria-label="Selected"
                            data-dayid={rsvp.dayId}
                            data-appointmentid={rsvp.appointmentId}
                            data-collegefairid={rsvp.collegeFairId}
                            label={{
                              htmlFor: 'itemsCheckBox',
                              children: 'Selected',
                            }}
                            checked={some(this.props.selectedAppointments, { id: rsvp.id })}
                            onChange={this.onSelect}
                          />
                        </div>
                        <div>
                          <EventAgenda event={rsvp} />
                        </div>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                ))}
              </ul>
              {this.props.selectBoxError && (
              <Label role="tooltip" basic styleName="validationError" pointing>
                <Icon name="exclamation circle" styleName="validationError" size="large" />
                <FormattedMessage {...messages.selectBoxErrorMessage} />
              </Label>
              )}
            </div>
          ): (
            <div styleName="noresults">
              <Message color="yellow">
                <Message.Header>
                  <Icon name="warning circle" size="large" color="grey" />
                  <p styleName="noresultsHeader">
                    {`${this.props.selectedStaffName} `}<FormattedMessage {...messages.noResultsHeading} />
                  </p>
                </Message.Header>
                <div>
                  <p styleName="noresultsContent"><FormattedMessage {...messages.noResultsContent} /></p>
                </div>
              </Message>
            </div>
          )}
        </div>
        <div styleName="showmore">
          {rsvps.length > 0 ? (
            <div>
              <p>Showing 1-{showingAppointments} of {`${rsvps.length}`} </p>
              {this.state.itemsToShow < rsvps.length ? (
                <Button
                  content="Show More"
                  onClick={this.showMore}
                />
              ): null}
            </div>
          ):
            null}
        </div>
      </div>
    );
  }
}

const query = gql`
  query appointmentsList($fromDate: OrdinalDate!, $toDate: OrdinalDate!, $heUserId: String) {
    viewer {
      id
      enabledFeatures
      repVisitsEvents(fromDate: $fromDate toDate: $toDate heUserId: $heUserId) {
        id
        date
        name
        timezone
        startTime
        startISOTime
        endTime
        endISOTime
        highSchool {
          id
          name
        }
        userBookingStatus
        collegeFairId
        appointmentId
        dayId
        notes
        chosenVisitLocationType
      }
    }
  }
`;

const graphqlData = graphql(query, {
  options: ({ heUserId }) => ({
    variables: {
      fromDate: format(new Date(), 'yyyy-MM-dd'),
      toDate: format(addMonths(new Date(), AppointmentsListComponent.defaultMonthsToFetch), 'yyyy-MM-dd'),
      heUserId,
    },
    fetchPolicy: 'network-only',
  }),
});

export default SimpleLoadingComponent(graphqlData)(AppointmentsListComponent);
